import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';

import { LoadingState } from '@atlas/base';
import { Bonus, BonusTitle, Slider } from '@atlas/components';
import { AccountManager, SiteConfigManager, useSettings } from '@atlas/core';
import { Icon, Label, Loader, Timer, ToggleCheckbox } from '@atlas/ui';

import { getBonusMinDeposit } from 'common/utils/depositBonusUtils';
import { CDN_TYPE_CRM, imageUrlCDN } from 'common/utils/utils';
import useBonusesService from 'hooks/useBonusesService';
import { BASE_PATH } from 'src/common/constants';
import { useBonusesContext } from 'src/components/BonusesProvider';
import { getMinDeposit, isAmountBelowMinDeposit } from 'src/components/BonusesProvider/utils';
import useBonusSliderAtEnd from 'src/hooks/useBonusSliderAtEnd';

const timerLabels = {
  days: 'timer_d',
  hours: 'timer_h',
  minutes: 'timer_m',
};

const DepositBonuses = () => {
  const bonusesService = useBonusesService();
  const { lang } = useSettings();
  const hideSportBonuses = SiteConfigManager.getConfig('config.config.general.hideSportBonuses');
  const currency = AccountManager.getAccountCurrency();

  const {
    bonusesList,
    depositAmount,
    isLockedBonuses,
    isValidatingBonus,
    paymentMethod,
    selectedBonus,
    setSelectedBonus,
  } = useBonusesContext();

  const minAvailableDepositAmount = useMemo(() => {
    if (bonusesList.length === 0) {
      return 0;
    }

    return bonusesList.reduce((min, bonus) => {
      const minDeposit = getBonusMinDeposit(bonus, currency);

      return min === Infinity ? minDeposit : Math.min(min, minDeposit);
    }, Infinity);
  }, [bonusesList, currency]);

  const minDepositFromSelected = getBonusMinDeposit(selectedBonus, currency);

  const minBonusAmount =
    minDepositFromSelected > 0 ? minDepositFromSelected : minAvailableDepositAmount;

  const sliderKey = useMemo(() => bonusesList.map(({ id }) => id).join('-'), [bonusesList]);

  const {
    atEnd: bonusSliderAtEnd,
    onSwiper: onBonusSwiper,
    scrollToSlide,
  } = useBonusSliderAtEnd(sliderKey);
  const selectedBonusId = selectedBonus?.id;

  const selectedBonusIndex = useMemo(
    () => bonusesList.findIndex(bonus => bonus.id === selectedBonusId),
    [bonusesList, selectedBonusId],
  );
  const hasSingleBonus = bonusesList.length === 1;

  useEffect(() => {
    if (hasSingleBonus || !selectedBonusId || !paymentMethod?.id) {
      return;
    }

    if (selectedBonusIndex >= 0) {
      scrollToSlide(selectedBonusIndex, { force: true });
    }
  }, [hasSingleBonus, paymentMethod?.id, scrollToSlide, selectedBonusId, selectedBonusIndex]);

  const handleSelectBonus = useCallback(
    bonus => {
      const isActive = selectedBonus?.id === bonus?.id;
      const newActiveBonus = isActive ? null : bonus;

      setSelectedBonus(newActiveBonus);
    },
    [selectedBonus, setSelectedBonus],
  );

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      loop: false,
      nextBtn: <Slider.NextBtn>next</Slider.NextBtn>,
      prevBtn: <Slider.PrevBtn>prev</Slider.PrevBtn>,
      slidesPerView: 'auto',
      spaceBetween: '8px',
    }),
    [],
  );

  if (bonusesService?.loading === LoadingState.loading) {
    return <Loader className="profile-deposit__loader" />;
  }

  if (!bonusesList.length) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = bonus?.financeDataList?.find(item => item.currency === currency);
    const freeSpinMinAmount = bonus?.freeSpinMinAmountsDeposit?.[currency];
    const image = bonus?.getImage(lang, 'ENG');

    const bonusImage = image
      ? `${imageUrlCDN(image, CDN_TYPE_CRM)}`
      : `${BASE_PATH}/static/assets/images/gift.png`;

    const bonusTitle = (
      <BonusTitle
        className="deposit-bonus-title"
        name={bonus?.name}
        nameHighlighted={bonus?.nameHighlighted}
      />
    );
    const isActive = selectedBonus?.id === bonus?.id;

    const bonusDescription = (
      <div className="deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: bonus?.currency,
            minBonusAmount: freeSpinMinAmount ?? finance?.minDeposit ?? 0,
          }}
        />
      </div>
    );

    const disableBlock = (
      <div className="bonus-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label className="bonus-disable-title" message="deposit_bonus_unavailable_title" />
        <Label className="bonus-disable-description" message="deposit_bonus_unavaliable_message" />
      </div>
    );

    const timer = bonus?.expirationTime && (
      <div className="timer">
        <Icon className="deposit-timer-icon" height={16} name="time" width={16} />
        <Timer
          labels={timerLabels}
          separator=":"
          showSeconds={false}
          time={bonus?.expirationTime}
        />
      </div>
    );

    const bonusLabel = !hideSportBonuses && (
      <Label className="deposit-bonus-label" message={`bonus_product_${bonus.productType}`} />
    );
    const isBelowMinDeposit = isAmountBelowMinDeposit(
      depositAmount,
      getMinDeposit(bonus, currency),
    );
    const isActiveSelection = isActive && !isValidatingBonus && !bonus?.disabled;

    return (
      <div
        className={classNames('deposit-bonus', {
          ['deposit-bonus--single']: hasSingleBonus,
        })}
        key={bonus?.id}
        onClick={() => handleSelectBonus(bonus)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleSelectBonus(bonus);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <Bonus
          active={isActiveSelection}
          description={bonusDescription}
          disabled={bonus?.disabled || false}
          disabledBlock={disableBlock}
          image={bonusImage}
          label={bonusLabel}
          timer={timer}
          title={bonusTitle}
          toggleCheckbox={
            <ToggleCheckbox
              checked={isActiveSelection}
              disabled={
                bonus?.disabled ||
                isBelowMinDeposit ||
                (isLockedBonuses && !isActive) ||
                isValidatingBonus
              }
              onChange={() => handleSelectBonus(bonus)}
            />
          }
        />
      </div>
    );
  };

  return (
    <div
      className={classNames('deposit-bonus-container', {
        single: hasSingleBonus,
      })}
    >
      {!hasSingleBonus && (
        <div className="slider-head">
          <Label className="slider-title" message="toggle_to_select" />
          <Label
            className="slider-description"
            message="deposit_to_activate"
            params={{
              currency,
              minBonusAmount,
            }}
          />
        </div>
      )}
      {hasSingleBonus && renderBonus(bonusesList[0])}
      {!hasSingleBonus && (
        <div
          className={classNames('bonus-slider-outer', {
            'bonus-slider-outer--at-end': bonusSliderAtEnd,
          })}
        >
          <Slider key={sliderKey} {...sliderConfig} onSwiper={onBonusSwiper}>
            {bonusesList.map(bonus => (
              <Slider.Slide className="deposit-bonus-slide" key={bonus?.id}>
                {renderBonus(bonus)}
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      )}
    </div>
  );
};

export default DepositBonuses;
